// store/slices/supportSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import supportService, {
  SupportMessage,
  SupportTicket,
} from "../../services/supportService";

interface ToastNotification {
  id: number;
  ticketId: number;
  ticketSubject: string;
  senderName: string;
  text: string;
}

interface SupportState {
  isOpen: boolean;
  activeTicketId: number | null;
  view: "list" | "new" | "chat";

  // ── User side ──────────────────────────────────────────────────────────────
  tickets: SupportTicket[];
  messagesByTicket: Record<number, SupportMessage[]>; // keyed by ticketId
  unreadCount: number;
  isTyping: boolean;
  typingUser: string | null;
  typingTicketId: number | null;

  // ── Admin side ─────────────────────────────────────────────────────────────
  adminTickets: any[];
  adminMessagesByTicket: Record<number, SupportMessage[]>; // keyed by ticketId — no more flat list + filter
  adminActiveTicket: any | null;
  adminStats: any | null;
  adminUnread: number;
  adminIsTyping: boolean;
  adminTypingUser: string | null;
  adminTypingTicketId: number | null;

  toasts: ToastNotification[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
}

const initialState: SupportState = {
  isOpen: false,
  activeTicketId: null,
  view: "list",
  tickets: [],
  messagesByTicket: {},
  unreadCount: 0,
  isTyping: false,
  typingUser: null,
  typingTicketId: null,
  adminTickets: [],
  adminMessagesByTicket: {},
  adminActiveTicket: null,
  adminStats: null,
  adminUnread: 0,
  adminIsTyping: false,
  adminTypingUser: null,
  adminTypingTicketId: null,
  toasts: [],
  isLoading: false,
  isSending: false,
  error: null,
};

// ── Thunks ─────────────────────────────────────────────────────────────────────

export const getUserTickets = createAsyncThunk(
  "support/getUserTickets",
  async (_, { rejectWithValue }) => {
    try {
      return await supportService.getUserTickets();
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
);

export const createTicket = createAsyncThunk(
  "support/createTicket",
  async (
    { subject, message }: { subject: string; message: string },
    { rejectWithValue },
  ) => {
    try {
      return await supportService.createTicket(subject, message);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
);

export const getTicketMessages = createAsyncThunk(
  "support/getTicketMessages",
  async (ticketId: number, { rejectWithValue }) => {
    try {
      const data = await supportService.getTicketMessages(ticketId);
      // Attach the ticketId we requested so the reducer always knows which bucket to write
      return { ...data, _ticketId: ticketId };
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
);

export const sendMessage = createAsyncThunk(
  "support/sendMessage",
  async (
    {
      ticketId,
      message,
      attachment,
    }: { ticketId: number; message: string | null; attachment?: File | null },
    { rejectWithValue },
  ) => {
    try {
      return await supportService.sendMessage(ticketId, message, attachment);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
);

export const closeTicket = createAsyncThunk(
  "support/closeTicket",
  async (ticketId: number, { rejectWithValue }) => {
    try {
      await supportService.closeTicket(ticketId);
      return ticketId;
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
);

export const adminGetAllTickets = createAsyncThunk(
  "support/adminGetAllTickets",
  async (filters: Record<string, string> | undefined, { rejectWithValue }) => {
    try {
      return await supportService.adminGetAllTickets(filters);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
);

export const adminGetTicketMessages = createAsyncThunk(
  "support/adminGetTicketMessages",
  async (ticketId: number, { rejectWithValue }) => {
    try {
      const data = await supportService.adminGetTicketMessages(ticketId);
      // Attach the ticketId we requested so the reducer always knows which bucket to write
      return { ...data, _ticketId: ticketId };
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
);

export const adminSendMessage = createAsyncThunk(
  "support/adminSendMessage",
  async (
    {
      ticketId,
      message,
      attachment,
    }: { ticketId: number; message: string | null; attachment?: File | null },
    { rejectWithValue },
  ) => {
    try {
      return await supportService.adminSendMessage(
        ticketId,
        message,
        attachment,
      );
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
);

export const adminUpdateStatus = createAsyncThunk(
  "support/adminUpdateStatus",
  async (
    {
      ticketId,
      status,
      priority,
    }: { ticketId: number; status: string; priority?: string },
    { rejectWithValue },
  ) => {
    try {
      return await supportService.adminUpdateStatus(ticketId, status, priority);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  },
);

// ── Helpers ────────────────────────────────────────────────────────────────────

function mergeMessages(
  existing: SupportMessage[],
  incoming: SupportMessage[],
): SupportMessage[] {
  const map = new Map<number, SupportMessage>();
  existing.forEach((m) => map.set(m.id, m));
  incoming.forEach((m) => map.set(m.id, m)); // incoming wins on conflict
  return Array.from(map.values()).sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
}

// ── Slice ──────────────────────────────────────────────────────────────────────

const supportSlice = createSlice({
  name: "support",
  initialState,
  reducers: {
    openSupportWidget: (state) => {
      state.isOpen = true;
    },
    closeSupportWidget: (state) => {
      state.isOpen = false;
    },

    setActiveTicketId: (state, action: PayloadAction<number | null>) => {
      state.activeTicketId = action.payload;
      state.view = action.payload ? "chat" : "list";
    },

    setSupportView: (state, action: PayloadAction<"list" | "new" | "chat">) => {
      state.view = action.payload;
    },

    // Called by Pusher on every incoming message (both sides)
    addIncomingMessage: (state, action: PayloadAction<SupportMessage>) => {
      const msg = action.payload;
      const tid = msg.ticket_id;
      const preview = msg.message
        ? msg.message.slice(0, 80)
        : msg.attachment_name
          ? `📎 ${msg.attachment_name}`
          : "";

      // ── User bucket ────────────────────────────────────────────────────────
      if (!state.messagesByTicket[tid]) state.messagesByTicket[tid] = [];
      if (!state.messagesByTicket[tid].some((m) => m.id === msg.id)) {
        state.messagesByTicket[tid].push(msg);
        const ticket = state.tickets.find((t) => t.id === tid);
        if (ticket) {
          ticket.latest_message = preview;
          ticket.last_message_at = msg.created_at;
          if (msg.sender_type === "admin") {
            ticket.unread_count = (ticket.unread_count || 0) + 1;
            // Recompute total from sum — never trust incremental arithmetic
            state.unreadCount = state.tickets.reduce(
              (sum, t) => sum + (t.unread_count || 0),
              0,
            );
          }
        }
      }

      // ── Admin bucket ───────────────────────────────────────────────────────
      if (!state.adminMessagesByTicket[tid])
        state.adminMessagesByTicket[tid] = [];
      if (!state.adminMessagesByTicket[tid].some((m) => m.id === msg.id)) {
        state.adminMessagesByTicket[tid].push(msg);
        const adminTicket = state.adminTickets.find((t) => t.id === tid);
        if (adminTicket) {
          adminTicket.latest_message = preview;
          adminTicket.last_message_at = msg.created_at;
          if (msg.sender_type === "user") {
            adminTicket.unread_count = (adminTicket.unread_count || 0) + 1;
            state.adminUnread = state.adminTickets.reduce(
              (sum, t) => sum + (t.unread_count || 0),
              0,
            );
          }
        }
      }
    },

    setTypingIndicator: (
      state,
      action: PayloadAction<{
        isTyping: boolean;
        senderName: string | null;
        ticketId?: number;
      }>,
    ) => {
      state.isTyping = action.payload.isTyping;
      state.typingUser = action.payload.senderName;
      state.typingTicketId = action.payload.ticketId ?? null;
    },

    setAdminTypingIndicator: (
      state,
      action: PayloadAction<{
        isTyping: boolean;
        senderName: string | null;
        ticketId?: number;
      }>,
    ) => {
      state.adminIsTyping = action.payload.isTyping;
      state.adminTypingUser = action.payload.senderName;
      state.adminTypingTicketId = action.payload.ticketId ?? null;
    },

    updateTicketStatus: (
      state,
      action: PayloadAction<{ ticketId: number; status: string }>,
    ) => {
      const t = state.tickets.find((t) => t.id === action.payload.ticketId);
      if (t) t.status = action.payload.status as any;
      const at = state.adminTickets.find(
        (t) => t.id === action.payload.ticketId,
      );
      if (at) at.status = action.payload.status;
      if (state.adminActiveTicket?.id === action.payload.ticketId) {
        state.adminActiveTicket = {
          ...state.adminActiveTicket,
          status: action.payload.status,
        };
      }
    },

    setAdminActiveTicket: (state, action: PayloadAction<any>) => {
      state.adminActiveTicket = action.payload;
    },

    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
    setAdminUnread: (state, action: PayloadAction<number>) => {
      state.adminUnread = action.payload;
    },

    showToast: (state, action: PayloadAction<ToastNotification>) => {
      state.toasts.push(action.payload);
    },
    dismissToast: (state, action: PayloadAction<number>) => {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },

    markTicketRead: (state, action: PayloadAction<number>) => {
      const ticket = state.tickets.find((t) => t.id === action.payload);
      if (ticket) {
        ticket.unread_count = 0;
        // Recompute from source of truth — never do arithmetic that can drift
        state.unreadCount = state.tickets.reduce(
          (sum, t) => sum + (t.unread_count || 0),
          0,
        );
      }
    },

    markAdminTicketRead: (state, action: PayloadAction<number>) => {
      const ticket = state.adminTickets.find((t) => t.id === action.payload);
      if (ticket) {
        ticket.unread_count = 0;
        state.adminUnread = state.adminTickets.reduce(
          (sum, t) => sum + (t.unread_count || 0),
          0,
        );
      }
    },

    clearSupportError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // ── getUserTickets ───────────────────────────────────────────────────────
      .addCase(getUserTickets.pending, (s) => {
        s.isLoading = true;
      })
      .addCase(getUserTickets.fulfilled, (s, a) => {
        s.isLoading = false;
        s.tickets = a.payload;
        s.unreadCount = a.payload.reduce(
          (sum, t) => sum + (t.unread_count || 0),
          0,
        );
      })
      .addCase(getUserTickets.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload as string;
      })

      // ── createTicket ─────────────────────────────────────────────────────────
      .addCase(createTicket.fulfilled, (s, a) => {
        if (a.payload) {
          s.tickets.unshift(a.payload);
          s.activeTicketId = a.payload.id;
          s.view = "chat";
        }
      })

      // ── getTicketMessages ────────────────────────────────────────────────────
      .addCase(getTicketMessages.pending, (s) => {
        s.isLoading = true;
      })
      .addCase(getTicketMessages.fulfilled, (s, a) => {
        s.isLoading = false;
        const tid: number = a.payload._ticketId;
        const fetched: SupportMessage[] = a.payload.messages || [];
        s.messagesByTicket[tid] = mergeMessages(
          s.messagesByTicket[tid] || [],
          fetched,
        );
        // Backend marks messages as read when getMessages is called — sync local state
        const ticket = s.tickets.find((t) => t.id === tid);
        if (ticket) {
          ticket.unread_count = 0;
          s.unreadCount = s.tickets.reduce(
            (sum, t) => sum + (t.unread_count || 0),
            0,
          );
        }
      })
      .addCase(getTicketMessages.rejected, (s) => {
        s.isLoading = false;
      })

      // ── sendMessage ──────────────────────────────────────────────────────────
      .addCase(sendMessage.pending, (s) => {
        s.isSending = true;
      })
      .addCase(sendMessage.fulfilled, (s, a) => {
        s.isSending = false;
        if (a.payload) {
          const tid = a.payload.ticket_id;
          if (!s.messagesByTicket[tid]) s.messagesByTicket[tid] = [];
          if (!s.messagesByTicket[tid].some((m) => m.id === a.payload.id)) {
            s.messagesByTicket[tid].push(a.payload);
          }
        }
      })
      .addCase(sendMessage.rejected, (s, a) => {
        s.isSending = false;
        s.error = a.payload as string;
      })

      // ── closeTicket ──────────────────────────────────────────────────────────
      .addCase(closeTicket.fulfilled, (s, a) => {
        const t = s.tickets.find((t) => t.id === a.payload);
        if (t) t.status = "closed";
      })

      // ── adminGetAllTickets ───────────────────────────────────────────────────
      .addCase(adminGetAllTickets.pending, (s) => {
        s.isLoading = true;
      })
      .addCase(adminGetAllTickets.fulfilled, (s, a) => {
        s.isLoading = false;
        s.adminTickets = a.payload.data || [];
        s.adminUnread = (a.payload.data || []).reduce(
          (sum: number, t: any) => sum + (t.unread_count || 0),
          0,
        );
      })
      .addCase(adminGetAllTickets.rejected, (s) => {
        s.isLoading = false;
      })

      // ── adminGetTicketMessages ───────────────────────────────────────────────
      .addCase(adminGetTicketMessages.pending, (s) => {
        s.isLoading = true;
      })
      .addCase(adminGetTicketMessages.fulfilled, (s, a) => {
        s.isLoading = false;
        const tid: number = a.payload._ticketId;
        const fetched: SupportMessage[] = a.payload.messages || [];
        s.adminMessagesByTicket[tid] = mergeMessages(
          s.adminMessagesByTicket[tid] || [],
          fetched,
        );
        // Backend marks messages as read — sync local state
        const adminTicket = s.adminTickets.find((t) => t.id === tid);
        if (adminTicket) {
          adminTicket.unread_count = 0;
          s.adminUnread = s.adminTickets.reduce(
            (sum, t) => sum + (t.unread_count || 0),
            0,
          );
        }
        // Update active ticket — preserve the existing object, patch with fresh data
        if (a.payload.ticket) {
          s.adminActiveTicket = {
            ...(s.adminActiveTicket || {}),
            ...a.payload.ticket,
          };
        }
      })
      .addCase(adminGetTicketMessages.rejected, (s) => {
        s.isLoading = false;
      })

      // ── adminSendMessage ─────────────────────────────────────────────────────
      .addCase(adminSendMessage.pending, (s) => {
        s.isSending = true;
      })
      .addCase(adminSendMessage.fulfilled, (s, a) => {
        s.isSending = false;
        if (a.payload) {
          const tid = a.payload.ticket_id;
          if (!s.adminMessagesByTicket[tid]) s.adminMessagesByTicket[tid] = [];
          if (
            !s.adminMessagesByTicket[tid].some((m) => m.id === a.payload.id)
          ) {
            s.adminMessagesByTicket[tid].push(a.payload);
          }
        }
      })
      .addCase(adminSendMessage.rejected, (s) => {
        s.isSending = false;
      })

      // ── adminUpdateStatus ────────────────────────────────────────────────────
      .addCase(adminUpdateStatus.fulfilled, (s, a) => {
        if (a.payload?.ticket) {
          const t = s.adminTickets.find((t) => t.id === a.payload.ticket.id);
          if (t) {
            t.status = a.payload.ticket.status;
            t.priority = a.payload.ticket.priority;
          }
          if (s.adminActiveTicket?.id === a.payload.ticket.id) {
            s.adminActiveTicket = {
              ...s.adminActiveTicket,
              ...a.payload.ticket,
            };
          }
        }
      });
  },
});

export const {
  openSupportWidget,
  closeSupportWidget,
  setActiveTicketId,
  setSupportView,
  addIncomingMessage,
  setTypingIndicator,
  setAdminTypingIndicator,
  updateTicketStatus,
  setAdminActiveTicket,
  setUnreadCount,
  setAdminUnread,
  showToast,
  dismissToast,
  markTicketRead,
  markAdminTicketRead,
  clearSupportError,
} = supportSlice.actions;

export default supportSlice.reducer;
